import React from "react";
import { useBusinessCentralCustomer } from "./hooks";
import { Loading } from "components/panels";
import { areAddressesTheSame, isSameString } from "utilities/businessCentralUtils";
import classNames from "classnames";
import { newLineToBr } from "utilities/newlinetobr";
import InPageEditableFieldWithLabel from "components/MainSectionTemplate/InPageEditableFieldWithLabel";
import { InPageEditableField } from "components/MainSectionTemplate/InPageEditableField";
import { ButtonText } from "components/Atoms/Button";
import { SomethingUnexpectedHappenedMessageBox } from "components/MessageBox/SomethingUnexpectedHappenedMessageBox";

interface Props {
  company: server.dto.Company
  businessCentralNumber: string
  onUnlink: () => void
}

const BusinessCentralCustomerRows = ({ businessCentralNumber, company, onUnlink }: Props) => {
  const hasBusinessCentralNumber = businessCentralNumber != null;

  const businessCentralQuery = useBusinessCentralCustomer(businessCentralNumber, {
    enabled: hasBusinessCentralNumber
  });

  if (!hasBusinessCentralNumber) {
    return <></>;
  }

  const isLoading = businessCentralQuery.isLoading;
  if (isLoading) {
    return <Loading />;
  }

  const businessCentralApiFailed = businessCentralQuery.isError;
  if (businessCentralApiFailed) {
    return <>
      <InPageEditableFieldWithLabel
        compact={false}
        field={{
          key: "bcError",
          label: "Error",
          render: () => <SomethingUnexpectedHappenedMessageBox />,
        }}
      />

      <UnlinkField onUnlink={onUnlink} />
    </>
  }

  const hasErrors = businessCentralQuery.data.errors.length > 0;
  if (hasErrors) {
    return <>
      <InPageEditableFieldWithLabel
        compact={false}
        field={{
          key: "bcError",
          label: "Error",
          render: () => <div className="ui message red">
            <div>{newLineToBr(businessCentralQuery.data.errors.map(e => e.message).join("\n"))}</div>
          </div>,
        }}
      />

      <UnlinkField onUnlink={onUnlink} />
    </>
  }

  const businessCentralData = businessCentralQuery.data.data;

  const isSameName = isSameString(businessCentralData.name, company.admName);
  const isSameAddress = areAddressesTheSame(businessCentralData.address, company.admAddress);
  const isSameVatNumber = isSameString(businessCentralData.vatNumber, company.euVatNumber?.number);
  const isSameBlockedState = true; // We have no blocked state in company

  const blockedLabel = businessCentralData.blocked ? RESX.GeneralLabels.Yes : RESX.GeneralLabels.No;

  return <>
    <InPageEditableFieldWithLabel
      compact={false}
      field={{
        key: "businessCentralName",
        label: RESX.GeneralLabels.Name,
        render: () => <div className={classNames({ red: !isSameName })}>{businessCentralData.name}</div>,
      }}
    />
    <InPageEditableFieldWithLabel
      compact={false}
      field={{
        key: "businessCentralAddress",
        label: RESX.Address.resxAddress,
        render: () => <div className={classNames({ red: !isSameAddress })}>
          <div>{businessCentralData.address.addressLine1}</div>
          {businessCentralData.address.addressLine2 && <div>{businessCentralData.address.addressLine2}</div>}
          <div>{businessCentralData.address.postalCode} {businessCentralData.address.city}</div>
          <div>{businessCentralData.address.countryName}</div>
        </div>,
      }}
    />

    {businessCentralData.vatNumber && <InPageEditableFieldWithLabel
      compact={false}
      field={{
        key: "businessCentralVatNumber",
        label: RESX.VatNumber.resxVATNumber,
        render: () => <div className={classNames({ red: !isSameVatNumber })}>{businessCentralData.vatNumber}</div>,
      }}
    />}

    <InPageEditableFieldWithLabel
      compact={false}
      field={{
        key: "businessCentralBlocked",
        label: "Blocked",
        render: () => <div className={classNames({ red: !isSameBlockedState })}>{blockedLabel}</div>,
      }}
    />

    <UnlinkField onUnlink={onUnlink} />
  </>;
};

const UnlinkField = ({ onUnlink }: { onUnlink: () => void }) => {
  if (!site.permissions.canConfigureSystemSettings) return <></>;

  return <InPageEditableField
    compact={false}
    field={{
      key: "bcUnlink",
      label: "",
      render: () => <ButtonText style={{ padding: 0 }} label={RESX.Buttons.btnRemoveLink} onClick={() => { onUnlink(); }} />,
    }}
  />
}

export default BusinessCentralCustomerRows;